import { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { HeartHandshake, RefreshCw, Mail, MessageSquare, Send } from "lucide-react";

const STATUT_LABELS = {
  suspendu: ["Suspendu", "bg-amber-500/15 text-amber-300 border-amber-500/30"],
  annule: ["Annulé", "bg-red-500/15 text-red-300 border-red-500/30"],
  "annulé": ["Annulé", "bg-red-500/15 text-red-300 border-red-500/30"],
  essai: ["Essai", "bg-cyan-500/15 text-cyan-300 border-cyan-500/30"],
  inactif: ["Inactif", "bg-zinc-500/15 text-zinc-300 border-zinc-500/30"],
};

export default function RetentionModule() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");

  const load = useCallback(() => {
    api.get("/retention/dashboard").then((r) => setData(r.data)).catch(() => toast.error("Erreur de chargement"));
  }, []);
  useEffect(() => { load(); }, [load]);

  const relance = async (row, channel) => {
    setBusy(row.contract_id + channel);
    try {
      const r = await api.post("/retention/relance", { contract_id: row.contract_id, channel });
      if (r.data.sent_any) toast.success(`Relance envoyée à ${row.full_name} ${r.data.emailed ? "· courriel" : ""}${r.data.smsed ? " · SMS" : ""}`);
      else toast.warning(`Relance enregistrée (envoi non abouti : ${(r.data.errors || []).join(" ; ") || "canal indisponible"})`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Échec de la relance."); }
    setBusy("");
  };

  const k = data?.kpis || {};
  const lapsed = data?.lapsed || [];

  return (
    <div className="p-6 space-y-6 text-zinc-100" data-testid="retention-module">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><HeartHandshake className="w-6 h-6 text-pink-400" /> Rétention & Réabonnement</h1>
          <p className="text-sm text-zinc-400 mt-1">Reconquiers les membres lapsés : relance de réabonnement en 1 clic (courriel + SMS).</p>
        </div>
        <button onClick={load} className="px-3 py-1.5 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-sm flex items-center gap-1" data-testid="retention-refresh"><RefreshCw className="w-4 h-4" /> Actualiser</button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          ["Membres lapsés", k.total_lapsed, "text-pink-300"],
          ["Jamais relancés", k.jamais_relances, "text-amber-300"],
          ["Avec courriel", k.avec_courriel, "text-emerald-300"],
          ["Relances envoyées", k.relances_envoyees, "text-cyan-300"],
        ].map(([label, val, cls]) => (
          <div key={label} className="rounded-xl border border-zinc-800 bg-zinc-900/60 p-4">
            <div className="text-xs text-zinc-400">{label}</div>
            <div className={`text-2xl font-bold ${cls}`}>{val ?? "—"}</div>
          </div>
        ))}
      </div>

      {!data?.twilio_configured && (
        <div className="text-xs text-amber-300/80 bg-amber-500/5 border border-amber-500/20 rounded-lg px-3 py-2">
          ⚠️ SMS (Twilio) non configuré — seules les relances par courriel seront envoyées. Le courriel utilise votre compte Gmail connecté.
        </div>
      )}

      <div className="rounded-xl border border-zinc-800 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-zinc-900/80 text-zinc-400 text-xs uppercase">
            <tr>
              <th className="text-left px-4 py-3">Membre</th>
              <th className="text-left px-4 py-3">Statut</th>
              <th className="text-left px-4 py-3">Inactif</th>
              <th className="text-left px-4 py-3">Relances</th>
              <th className="text-right px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {lapsed.length === 0 && (
              <tr><td colSpan={5} className="px-4 py-8 text-center text-zinc-500" data-testid="retention-empty">🎉 Aucun membre lapsé — rétention à 100 %.</td></tr>
            )}
            {lapsed.map((row) => {
              const [lbl, cls] = STATUT_LABELS[row.statut] || [row.statut, "bg-zinc-500/15 text-zinc-300 border-zinc-500/30"];
              return (
                <tr key={row.contract_id} data-testid={`retention-row-${row.contract_id}`} className="border-t border-zinc-800/70 hover:bg-zinc-900/40">
                  <td className="px-4 py-3">
                    <div className="font-medium">{row.full_name}</div>
                    <div className="text-xs text-zinc-500">{row.discipline || "—"}{row.email ? ` · ${row.email}` : ""}</div>
                  </td>
                  <td className="px-4 py-3"><span className={`text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full border ${cls}`}>{lbl}</span></td>
                  <td className="px-4 py-3 text-zinc-300">{row.months_inactive != null ? `${row.months_inactive} mois` : "—"}</td>
                  <td className="px-4 py-3">
                    {row.relance_count > 0
                      ? <span className="text-emerald-400 text-xs">{row.relance_count}× · {new Date(row.last_relance_at).toLocaleDateString("fr-CA")}</span>
                      : <span className="text-zinc-500 text-xs">Aucune</span>}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1.5 justify-end">
                      <button title="Relancer par courriel" disabled={!row.email || busy} onClick={() => relance(row, "email")} data-testid={`relance-email-${row.contract_id}`} className="p-2 rounded-lg bg-zinc-800 hover:bg-zinc-700 disabled:opacity-40"><Mail className="w-4 h-4" /></button>
                      <button title="Relancer par SMS" disabled={!row.phone || busy} onClick={() => relance(row, "sms")} data-testid={`relance-sms-${row.contract_id}`} className="p-2 rounded-lg bg-zinc-800 hover:bg-zinc-700 disabled:opacity-40"><MessageSquare className="w-4 h-4" /></button>
                      <button title="Relancer (courriel + SMS)" disabled={busy} onClick={() => relance(row, "both")} data-testid={`relance-both-${row.contract_id}`} className="px-3 py-2 rounded-lg bg-pink-500 hover:bg-pink-400 text-zinc-950 text-xs font-medium flex items-center gap-1"><Send className="w-3.5 h-3.5" /> Relancer</button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
